/**
 * JOCartProSection — Pro-account section rendered inside the cart drawer
 * footer (right before the "Passer au paiement" button).
 *
 * Behavior :
 * - Fetches /api/jardins-ole/crm/me/credit-status when the customer is
 *   logged in and the cart has items.
 * - Pro under credit limit : shows HT/TVA/TTC breakdown + "Facturer à 30j fin de mois" button
 *   that calls POST /me/on-account-order then clears the cart and redirects to confirmation.
 * - Pro at or above limit (but allowed Stripe) : shows a soft notice "Encours élevé,
 *   paiement immédiat requis".
 * - Pro hard-blocked (outstanding > limit) : full red banner with company phone/email.
 *
 * Non-particuliers + non-pros render nothing.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Phone, Mail, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/lib/useCart";
import { useCustomerAuth } from "@/lib/useCustomerAuth";
import { useSettings } from "@/lib/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSTOMER_TOKEN_KEY = "msc_customer_token";

function authHeaders() {
  const token = localStorage.getItem(CUSTOMER_TOKEN_KEY) || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt(c) {
  return `${(c / 100).toFixed(2)} €`;
}

export default function JOCartProSection() {
  const { items, subtotal_cents, drawerOpen, clear } = useCart();
  const { customer, isAuthenticated } = useCustomerAuth();
  const { settings: s } = useSettings();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (!drawerOpen || !isAuthenticated || !customer || items.length === 0) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/jardins-ole/crm/me/credit-status`, {
        params: { cart_total_cents: subtotal_cents },
        headers: authHeaders(),
      })
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [drawerOpen, isAuthenticated, customer?.id, subtotal_cents, items.length]);

  if (!status || !status.is_pro) return null;

  const phone = s?.universe_paysagiste?.phone || s?.phone;
  const email = s?.email;
  const hardBlock = !!status.blocked;
  const canOnAccount = (status.available_methods || []).includes("on_account");

  const placeOrder = async () => {
    if (!window.confirm(`Confirmer la commande à crédit (${fmt(subtotal_cents)} TTC) avec règlement à ${termsLabel(status.payment_terms)} ?`)) return;
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        fulfilment_method: "click_collect",
        shipping: { method: "click_collect" },
      };
      const r = await axios.post(`${API}/jardins-ole/crm/me/on-account-order`, payload, {
        headers: authHeaders(),
      });
      toast.success(`Commande ${r.data.order_number} créée — échéance ${r.data.due_date}`);
      clear();
      navigate(`/account/order-confirmation?order=${r.data.order_id}&on_account=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de création de commande");
    } finally {
      setPlacing(false);
    }
  };

  if (hardBlock) {
    return (
      <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3.5 space-y-2.5" data-testid="cart-pro-blocked">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-red-800">Encours dépassé</div>
            <div className="text-xs text-red-700 mt-0.5">
              Votre encours actuel est de <strong>{fmt(status.outstanding_cents)}</strong>,
              au-delà de votre limite autorisée de <strong>{fmt(status.credit_limit_cents)}</strong>.
              Merci de régulariser avant toute nouvelle commande en ligne.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {phone && (
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 rounded-full" data-testid="cart-pro-blocked-phone">
              <Phone className="w-3.5 h-3.5" /> Appeler
            </a>
          )}
          {email && (
            <a href={`mailto:${email}?subject=Encours%20commande%20professionnelle`} className="flex items-center justify-center gap-1.5 bg-red-700 hover:bg-red-800 text-white text-xs font-bold py-2 rounded-full" data-testid="cart-pro-blocked-email">
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
          )}
        </div>
      </div>
    );
  }

  // Pro under limit — show breakdown HT/TVA + optional on-account button
  const ttc = subtotal_cents;
  const ht = Math.round(ttc / 1.20);
  const tax = ttc - ht;

  return (
    <div className="rounded-xl border border-[color:var(--brand-sky)]/30 bg-sky-50/60 p-3.5 space-y-2.5" data-testid="cart-pro-section">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold text-[color:var(--brand-navy)] uppercase tracking-wider">Compte pro</span>
        <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
          {status.company_name || ""}
        </span>
      </div>
      {/* HT / TVA / TTC breakdown for VAT-registered pros */}
      {status.vat_number && (
        <div className="space-y-0.5 text-[11px] text-[hsl(var(--muted-foreground))]" data-testid="cart-pro-vat-breakdown">
          <div className="flex justify-between"><span>Net HT</span><span className="tabular-nums">{fmt(ht)}</span></div>
          <div className="flex justify-between"><span>TVA 20 %</span><span className="tabular-nums">{fmt(tax)}</span></div>
          <div className="flex justify-between font-bold text-[color:var(--brand-navy)] pt-0.5 border-t border-sky-200">
            <span>Total TTC</span><span className="tabular-nums">{fmt(ttc)}</span>
          </div>
        </div>
      )}
      {/* Outstanding info */}
      {status.credit_limit_cents > 0 && (
        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
          Encours : <strong className="text-[color:var(--brand-navy)]">{fmt(status.outstanding_cents)}</strong>
          {" "}/ Limite : <strong>{fmt(status.credit_limit_cents)}</strong>
          {" "}· Reste : <strong className="text-emerald-700">{fmt(status.remaining_credit_cents)}</strong>
        </div>
      )}
      {/* On-account button (only when allowed) */}
      {canOnAccount ? (
        <button
          type="button"
          onClick={placeOrder}
          disabled={placing || loading}
          className="w-full h-11 rounded-full bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy)]/90 text-white font-bold text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="cart-pro-on-account-btn"
        >
          {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Facturer à {termsLabel(status.payment_terms)}
        </button>
      ) : (
        <div className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="cart-pro-immediate-only">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Ce panier dépasse votre limite autorisée — paiement immédiat requis (option {termsLabel(status.payment_terms)} indisponible).</span>
        </div>
      )}
      {canOnAccount && (
        <div className="text-[10px] text-center text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
          ou règlement immédiat en CB ci-dessous
        </div>
      )}
    </div>
  );
}

function termsLabel(t) {
  return {
    immediate: "paiement immédiat",
    net_30: "30 jours",
    net_45: "45 jours",
    net_60: "60 jours",
    eom_30: "30 j fin de mois",
  }[t] || t || "—";
}
